import { useParams, useNavigate } from 'react-router-dom'
import { FaArrowLeft, FaCreditCard, FaCheckCircle, FaExclamationTriangle, FaComments, FaTimesCircle, FaCheckDouble, FaMapMarkerAlt, FaClock, FaDollarSign } from 'react-icons/fa'
import { useServiceRequest, useCancelServiceRequest, useConfirmSatisfaction } from '@/hooks/useRequests'
import Button from '@/components/common/Button/Button'
import routes from '@/routes/routes'
import './RequestDetail.css'

const getStatusInfo = (status: string) => {
    const statusMap: Record<string, { label: string; color: string; icon: any }> = {
        'slot_blocked': { label: 'Esperando Aprobación del Proveedor', color: '#F59E0B', icon: FaClock },
        'pending': { label: 'Pendiente', color: '#F59E0B', icon: FaClock },
        'approved': { label: 'Aprobada - Esperando Pago', color: '#3B82F6', icon: FaCheckCircle },
        'accepted': { label: 'Aceptada', color: '#3B82F6', icon: FaCheckCircle },
        'payment_submitted': { label: 'Pago Enviado - Esperando Verificación', color: '#8B5CF6', icon: FaCreditCard },
        'confirmed': { label: 'Confirmada', color: '#10B981', icon: FaCheckDouble },
        'in_progress': { label: 'En Progreso', color: '#8B5CF6', icon: FaCheckDouble },
        'completed': { label: 'Completada', color: '#10B981', icon: FaCheckDouble },
        'finished': { label: 'Finalizada', color: '#10B981', icon: FaCheckDouble },
        'cancelled_by_provider': { label: 'Cancelada por Proveedor', color: '#EF4444', icon: FaTimesCircle },
        'cancelled_by_client': { label: 'Cancelada por Ti', color: '#EF4444', icon: FaTimesCircle },
        'cancelled': { label: 'Cancelada', color: '#EF4444', icon: FaTimesCircle },
        'denied_by_provider': { label: 'Rechazada por Proveedor', color: '#6B7280', icon: FaTimesCircle },
        'rejected': { label: 'Rechazada', color: '#6B7280', icon: FaTimesCircle },
    }
    return statusMap[status] || { label: status, color: '#6B7280', icon: FaClock }
}

const RequestDetail = () => {
    const { id } = useParams()
    const requestId = Number(id)
    const navigate = useNavigate()
    const { data, isLoading, isError } = useServiceRequest(requestId)
    const cancelReq = useCancelServiceRequest()
    const confirm = useConfirmSatisfaction()

    const statusInfo = data ? getStatusInfo(data.status) : null
    const StatusIcon = statusInfo?.icon

    // Determine available actions based on status
    const canUploadPayment = data?.status === 'approved'
    const canConfirm = data?.status === 'completed' && !data.client_confirmed_satisfaction
    // Show chat button if conversation exists and request is not cancelled/denied
    // Allow chat for all active statuses (not just specific ones)
    const canViewConversation = data?.conversation_id && 
        !['cancelled_by_provider', 'cancelled_by_client', 'cancelled', 'denied_by_provider', 'rejected'].includes(data?.status || '')
    const canCancel = data && !['completed', 'finished', 'cancelled', 'cancelled_by_client', 'cancelled_by_provider', 'denied_by_provider'].includes(data.status)

    return (
        <div className="request-detail-page">
            {/* Header */}
            <div className="request-detail-header">
                <Button variant="ghost" onClick={() => navigate(-1)}>
                    <FaArrowLeft /> Atrás
                </Button>
                <div className="request-detail-header-content">
                    <h1 className="request-detail-title">Detalle del Pedido #{id}</h1>
                    <p className="request-detail-subtitle">Información completa de tu solicitud</p>
                </div>
            </div>
            
            <div className="request-detail-container">
                {isLoading ? (
                    <div className="request-detail-loading"><p>Cargando...</p></div>
                ) : isError ? (
                    <div className="request-detail-error"><p>Error al cargar pedido</p></div>
                ) : data ? (
                    <div className="request-detail-content">
                        {/* Status Banner */}
                        {statusInfo && StatusIcon && (
                            <div 
                                className="request-detail-status-banner"
                                style={{ backgroundColor: `${statusInfo.color}20` }}
                            >
                                <StatusIcon style={{ color: statusInfo.color }} />
                                <span style={{ color: statusInfo.color }}>{statusInfo.label}</span>
                            </div>
                        )}

                        {/* Service Info Card */}
                        <div className="request-detail-card">
                            <h2 className="request-detail-service-name">{data.service_name || data.service?.name || 'Servicio'}</h2>
                            
                            {data.description && (
                                <p className="request-detail-description">{data.description}</p>
                            )}

                            <div className="request-detail-info-grid">
                                {data.estimated_price && (
                                    <div className="request-detail-info-item">
                                        <FaDollarSign className="request-detail-info-icon" />
                                        <div>
                                            <span className="request-detail-info-label">Precio Estimado</span>
                                            <span className="request-detail-info-value">S/ {data.estimated_price.toFixed(2)}</span>
                                        </div>
                                    </div>
                                )}

                                {data.scheduled_date && (
                                    <div className="request-detail-info-item">
                                        <FaClock className="request-detail-info-icon" />
                                        <div>
                                            <span className="request-detail-info-label">Fecha y Hora</span>
                                            <span className="request-detail-info-value">
                                                {(() => {
                                                    // Parse date string as local date to avoid timezone shift
                                                    const dateStr = data.scheduled_date
                                                    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                                                        const [year, month, day] = dateStr.split('-').map(Number)
                                                        return new Date(year, month - 1, day).toLocaleDateString('es-ES')
                                                    }
                                                    return new Date(dateStr).toLocaleDateString('es-ES')
                                                })()}
                                                {data.scheduled_hour !== undefined && data.scheduled_hour !== null && (
                                                    <>
                                                        {' - '}
                                                        {String(data.scheduled_hour).padStart(2, '0')}:00
                                                        {data.estimated_duration_hours && (
                                                            <> - {String(data.scheduled_hour + data.estimated_duration_hours).padStart(2, '0')}:00 ({data.estimated_duration_hours}h)</>
                                                        )}
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {data.location_address && (
                                    <div className="request-detail-info-item">
                                        <FaMapMarkerAlt className="request-detail-info-icon" />
                                        <div>
                                            <span className="request-detail-info-label">Ubicación</span>
                                            <span className="request-detail-info-value">{data.location_address}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="request-detail-actions">
                            {canUploadPayment && (
                                <Button 
                                    variant="primary" 
                                    size="lg"
                                    onClick={() => navigate(routes.paymentUploadWithId.replace(":requestId", String(requestId)))}
                                >
                                    <FaCreditCard /> Subir Comprobante de Pago
                                </Button>
                            )}

                            {canViewConversation && (
                                <Button 
                                    variant="primary" 
                                    size="lg"
                                    onClick={() => navigate(routes.chat.replace(':id', String(data.conversation_id)))}
                                >
                                    <FaComments /> Ver Conversación
                                </Button>
                            )}

                            {canConfirm && (
                                <>
                                    <Button 
                                        variant="success" 
                                        size="lg"
                                        onClick={() => confirm.mutateAsync({ requestId, satisfied: true })} 
                                        disabled={confirm.isPending}
                                    >
                                        <FaCheckCircle /> Confirmar Satisfacción
                                    </Button>
                                    <Button 
                                        variant="warning" 
                                        size="lg"
                                        onClick={() => navigate(routes.complaintCreate, { state: { requestId } })}
                                    >
                                        <FaExclamationTriangle /> Reportar Problema
                                    </Button>
                                </>
                            )}

                            {canCancel && (
                                <Button 
                                    variant="danger" 
                                    size="lg"
                                    onClick={() => {
                                        if (window.confirm('¿Estás seguro de que quieres cancelar esta solicitud?')) {
                                            cancelReq.mutateAsync({ requestId, reason: 'Cancelación del cliente' })
                                        }
                                    }} 
                                    disabled={cancelReq.isPending}
                                >
                                    <FaTimesCircle /> Cancelar Solicitud
                                </Button>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default RequestDetail


